import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FaBuilding, FaUsers, FaCalendarCheck } from 'react-icons/fa';
import { headingFont } from 'theme/tokens';
import { translate } from 'i18n';

interface MigrationTimelineProps {
  /** Compact mode for embedding inside other pages */
  compact?: boolean;
}

const STEPS = [
  {
    icon: FaBuilding,
    dayKey: 'marketing.migration_day1',
    titleKey: 'marketing.migration_step1_title',
    descKey: 'marketing.migration_step1_desc',
    color: 'brand.500',
    bg: 'brand.50',
  },
  {
    icon: FaUsers,
    dayKey: 'marketing.migration_day2',
    titleKey: 'marketing.migration_step2_title',
    descKey: 'marketing.migration_step2_desc',
    color: 'accent.500',
    bg: 'green.50',
  },
  {
    icon: FaCalendarCheck,
    dayKey: 'marketing.migration_day3',
    titleKey: 'marketing.migration_step3_title',
    descKey: 'marketing.migration_step3_desc',
    color: 'purple.500',
    bg: 'purple.50',
  },
] as const;

export default function MigrationTimeline({ compact = false }: MigrationTimelineProps) {
  return (
    <Box>
      {!compact && (
        <Text
          fontFamily={headingFont}
          fontWeight="700"
          fontSize={{ base: 'lg', md: 'xl' }}
          color="gray.800"
          mb={6}
          textAlign="center"
        >
          {translate('marketing.migration_timeline_title')}
        </Text>
      )}

      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 0, md: 6 }}
        position="relative"
      >
        {STEPS.map((step, i) => (
          <Flex
            key={i}
            flex={1}
            direction={{ base: 'row', md: 'column' }}
            align={{ base: 'flex-start', md: 'center' }}
            gap={{ base: 4, md: 0 }}
            position="relative"
          >
            {/* Connector line (between steps) */}
            {i < STEPS.length - 1 && (
              <>
                {/* Horizontal connector (desktop) */}
                <Box
                  display={{ base: 'none', md: 'block' }}
                  position="absolute"
                  top="24px"
                  left="calc(50% + 24px)"
                  right="calc(-50% + 24px)"
                  h="2px"
                  bg="gray.200"
                />
                {/* Vertical connector (mobile) */}
                <Box
                  display={{ base: 'block', md: 'none' }}
                  position="absolute"
                  left="23px"
                  top="48px"
                  bottom="-4px"
                  w="2px"
                  bg="gray.200"
                />
              </>
            )}

            {/* Icon circle */}
            <Flex
              w="48px"
              h="48px"
              borderRadius="full"
              bg={step.bg}
              align="center"
              justify="center"
              flexShrink={0}
              zIndex={1}
              mb={{ base: 0, md: 3 }}
            >
              <Icon as={step.icon} color={step.color} boxSize={5} />
            </Flex>

            {/* Content */}
            <VStack
              spacing={1}
              align={{ base: 'flex-start', md: 'center' }}
              textAlign={{ base: 'left', md: 'center' }}
              pb={{ base: 6, md: 0 }}
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                color={step.color}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {translate(step.dayKey as any)}
              </Text>
              <Text
                fontFamily={headingFont}
                fontWeight="700"
                fontSize={compact ? 'sm' : 'md'}
                color="gray.800"
              >
                {translate(step.titleKey as any)}
              </Text>
              <Text fontSize="xs" color="gray.500" maxW="200px">
                {translate(step.descKey as any)}
              </Text>
            </VStack>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}
